import { useEffect, useMemo, useState } from "react";
import { X, ArrowsClockwise, Money } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { printReceipt } from "@/lib/printReceipt";

const PASS_DURATIONS = [
  { type: "Daily",       days: 1 },
  { type: "Weekly",      days: 7 },
  { type: "Monthly",     days: 30 },
  { type: "Quarterly",   days: 90 },
  { type: "Half Yearly", days: 180 },
  { type: "Yearly",      days: 365 },
];

const CARD_DURATIONS = [
  { type: "General Monthly",     days: 30 },
  { type: "General Quarterly",   days: 90 },
  { type: "General Half Yearly", days: 180 },
  { type: "General Yearly",      days: 365 },
];

const TOPUP_PRESETS = [100, 200, 500, 1000, 2000, 5000];

/* ---------------------------- PASS RENEW MODAL ---------------------------- */
export function PassRenewModal({ pass, onClose, onDone }) {
  const [opts, setOpts] = useState({ fares: {} });
  const [passType, setPassType] = useState(pass?.pass_type || "Monthly");
  const [amount, setAmount] = useState(pass?.amount || 0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/passes/options").then(({ data }) => setOpts(data));
  }, []);

  const days = PASS_DURATIONS.find(d => d.type === passType)?.days || 30;
  const newValidTo = useMemo(() => {
    const base = new Date(pass?.valid_to || Date.now());
    if (base < new Date()) base.setTime(Date.now());
    base.setDate(base.getDate() + days);
    return base.toISOString().slice(0, 10);
  }, [pass?.valid_to, days]);

  useEffect(() => {
    const entry = opts.fares?.[pass?.category];
    if (!entry) return;
    let fare = null;
    if (entry && typeof entry === "object" && entry[pass?.residency]) {
      fare = entry[pass.residency]?.[passType] ?? null;
    } else if (entry?.[passType] !== undefined) {
      fare = entry[passType];
    }
    if (fare !== null) setAmount(fare);
  }, [passType, opts.fares, pass?.category, pass?.residency]);

  const submit = async () => {
    if (!amount || amount < 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/passes/${pass.id}/renew`, { days, amount });
      toast.success(`Pass ${data.pass_no} renewed · valid till ${data.new_valid_to}`);
      printReceipt({ kind: "pass-renew", data: { ...pass, ...data, pass_type: passType, days } });
      onDone?.();
      onClose();
    } catch {
      toast.error("Renew failed");
    } finally { setBusy(false); }
  };

  if (!pass) return null;
  return (
    <ModalShell title="Renew Pass" subtitle={`${pass.pass_no} · ${pass.holder_name}`} onClose={onClose}>
      <KvRow label="Category" value={pass.category} />
      <KvRow label="Current Valid Till" value={pass.valid_to} />
      <KvRow label="Status" value={pass.status} />
      <Section title="Renew For">
        <div className="grid grid-cols-3 gap-2">
          {PASS_DURATIONS.map(d => (
            <button key={d.type} data-testid={`pass-renew-${d.type.toLowerCase().replace(/ /g,"-")}`}
                    onClick={() => setPassType(d.type)}
                    className={`px-3 py-2 border rounded-md text-sm transition-colors ${passType===d.type?"border-[#1E3A8A] bg-blue-50 font-semibold":"border-slate-200 hover:bg-slate-50"}`}>
              {d.type}
              <div className="text-[10px] text-slate-500">+{d.days}d</div>
            </button>
          ))}
        </div>
      </Section>
      <Section title="Amount (₹)">
        <input data-testid="pass-renew-amount" type="number" value={amount}
               onChange={e => setAmount(+e.target.value)}
               className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
      </Section>
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm flex items-center justify-between">
        <div>
          <div className="text-[11px] text-slate-500">New valid till</div>
          <div className="font-display font-bold text-base text-[#1E3A8A]">{newValidTo}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-slate-500">Pay</div>
          <div className="font-display font-bold text-2xl text-[#1E3A8A]">₹{amount}</div>
        </div>
      </div>
      <Footer onClose={onClose} onSubmit={submit} busy={busy}
              ctaLabel={<><ArrowsClockwise size={14}/> Renew · ₹{amount}</>} testid="pass-renew-submit"/>
    </ModalShell>
  );
}

/* ---------------------------- CARD TOPUP MODAL ---------------------------- */
export function CardTopupModal({ card, onClose, onDone }) {
  const [amount, setAmount] = useState(500);
  const [busy, setBusy] = useState(false);
  if (!card) return null;
  const balance = card.balance ?? card.amount ?? 0;
  const newBalance = +(balance) + (+amount || 0);

  const submit = async () => {
    if (!amount || amount < 100) { toast.error("Minimum top-up is ₹100"); return; }
    if (amount > 10000) { toast.error("Maximum top-up is ₹10,000 per transaction"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/topup`, { amount: +amount });
      toast.success(`Top-up ₹${amount} · balance ₹${data.new_balance}`);
      printReceipt({ kind: "card-topup", data: { ...card, ...data, topup_amount: +amount } });
      onDone?.();
      onClose();
    } catch {
      toast.error("Top-up failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Top-up Smart Card" subtitle={`${card.card_no} · ${card.holder_name}`} onClose={onClose}>
      <KvRow label="Card Type" value={card.card_type} />
      <KvRow label="Current Balance" value={`₹${balance}`} highlight/>
      <Section title="Choose Amount">
        <div className="grid grid-cols-3 gap-2">
          {TOPUP_PRESETS.map(p => (
            <button key={p} data-testid={`card-topup-preset-${p}`}
                    onClick={() => setAmount(p)}
                    className={`px-3 py-2 border rounded-md text-sm font-semibold transition-colors ${+amount===p?"border-[#1E3A8A] bg-blue-50 text-[#1E3A8A]":"border-slate-200 hover:bg-slate-50"}`}>
              ₹{p}
            </button>
          ))}
        </div>
      </Section>
      <Section title="Or enter custom amount (₹)">
        <input data-testid="card-topup-amount" type="number" min="1" value={amount}
               onChange={e=>setAmount(e.target.value === "" ? "" : +e.target.value)}
               className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
      </Section>
      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-slate-600">Balance after top-up</span>
          <span className="font-display font-bold text-2xl text-emerald-700">₹{newBalance}</span>
        </div>
      </div>
      <Footer onClose={onClose} onSubmit={submit} busy={busy}
              ctaLabel={<><Money size={14}/> Top-up · ₹{amount}</>} testid="card-topup-submit"/>
    </ModalShell>
  );
}

/* ---------------------------- CARD RENEW MODAL ---------------------------- */
export function CardRenewModal({ card, onClose, onDone }) {
  const [cardType, setCardType] = useState(card?.card_type || "General Monthly");
  const [opts, setOpts] = useState({ fares: {} });
  const [amount, setAmount] = useState(card?.amount || 0);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/cards/options").then(({ data }) => setOpts(data)); }, []);
  useEffect(() => {
    const fare = opts.fares?.[cardType];
    if (fare !== undefined) setAmount(fare);
  }, [cardType, opts.fares]);

  const days = CARD_DURATIONS.find(d => d.type === cardType)?.days || 30;
  const newValidTo = useMemo(() => {
    const base = new Date(card?.valid_to || card?.issued_at || Date.now());
    if (base < new Date()) base.setTime(Date.now());
    base.setDate(base.getDate() + days);
    return base.toISOString().slice(0, 10);
  }, [card?.valid_to, card?.issued_at, days]);

  if (!card) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/renew`, { days, amount: +amount });
      toast.success(`Card ${data.card_no} renewed · valid till ${data.new_valid_to}`);
      printReceipt({ kind: "card-renew", data: { ...card, ...data, card_type: cardType } });
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renew failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Renew Smart Card" subtitle={`${card.card_no} · ${card.holder_name}`} onClose={onClose}>
      <KvRow label="Current Type" value={card.card_type} />
      <KvRow label="Current Valid Till" value={card.valid_to || "—"} />
      <Section title="Renew For">
        <div className="grid grid-cols-2 gap-2">
          {CARD_DURATIONS.map(d => (
            <button key={d.type} data-testid={`card-renew-${d.type.toLowerCase().replace(/ /g,"-")}`}
                    onClick={() => setCardType(d.type)}
                    className={`px-3 py-2 border rounded-md text-sm transition-colors ${cardType===d.type?"border-[#1E3A8A] bg-blue-50 font-semibold":"border-slate-200 hover:bg-slate-50"}`}>
              {d.type}
              <div className="text-[10px] text-slate-500">+{d.days}d</div>
            </button>
          ))}
        </div>
      </Section>
      <Section title="Amount (₹)">
        <input data-testid="card-renew-amount" type="number" value={amount}
               onChange={e => setAmount(+e.target.value)}
               className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
      </Section>
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm flex items-center justify-between">
        <div>
          <div className="text-[11px] text-slate-500">New valid till</div>
          <div className="font-display font-bold text-base text-[#1E3A8A]">{newValidTo}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-slate-500">Pay</div>
          <div className="font-display font-bold text-2xl text-[#1E3A8A]">₹{amount}</div>
        </div>
      </div>
      <Footer onClose={onClose} onSubmit={submit} busy={busy}
              ctaLabel={<><ArrowsClockwise size={14}/> Renew · ₹{amount}</>} testid="card-renew-submit"/>
    </ModalShell>
  );
}

/* ----------------------------- shared building blocks ----------------------------- */
const ModalShell = ({ title, subtitle, onClose, children }) => (
  <div className="fixed inset-0 bg-black/50 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
    <div data-testid="renew-topup-modal" className="bg-white w-full max-w-lg max-h-[90dvh] sm:max-h-[92vh] overflow-auto rounded-t-2xl sm:rounded-lg" onClick={e => e.stopPropagation()}>
      <div className="sticky top-0 bg-white p-4 sm:p-5 border-b border-slate-200 flex items-start justify-between z-10 rounded-t-2xl sm:rounded-t-lg">
        <div>
          <h3 className="font-display text-xl font-bold">{title}</h3>
          <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  </div>
);

const KvRow = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={`tabular-nums ${highlight?"font-display font-bold text-base text-[#1E3A8A]":"font-medium text-slate-800"}`}>{value}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="label-cap text-slate-500 mb-2">{title}</div>
    {children}
  </div>
);

const Footer = ({ onClose, onSubmit, busy, ctaLabel, testid }) => (
  <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-5 px-5 py-3 mt-2 flex justify-end gap-2">
    <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Cancel</button>
    <button data-testid={testid} disabled={busy} onClick={onSubmit}
            className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
      {busy ? "Processing…" : ctaLabel}
    </button>
  </div>
);
